/**
 * MarketBrewer LLC - Header Component
 *
 * Features:
 * - Dark theme with DC Metro accent colors
 * - Responsive navigation with mobile hamburger menu
 * - Bilingual support (EN/ES)
 * - Language switcher
 * - Sticky header with backdrop blur
 * - CTA button
 * - Accessible navigation
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import LanguageSwitcher from "@/components/Navigation/LanguageSwitcher";
import Container from "@/components/Shared/Container";
import { CONTACT } from "@/constants";

interface HeaderProps {
  language: "en" | "es";
}

interface NavLink {
  to: string;
  label: string;
}

const Header: React.FC<HeaderProps> = ({ language }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // Refs for focus management — A11y-grade dialog requires moving focus into the
  // menu on open, trapping Tab inside while open, and returning focus to the
  // trigger on close.
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Navigation links based on language
  const navLinks: NavLink[] =
    language === "en"
      ? [
          { to: "/", label: "Home" },
          { to: "/services", label: "Services" },
          { to: "/services/photography", label: "Photography" },
          { to: "/about", label: "About" },
          { to: "/hire-me", label: "Hire Me" },
          { to: "/contact", label: "Contact" },
        ]
      : [
          { to: "/es", label: "Inicio" },
          { to: "/es/servicios", label: "Servicios" },
          { to: "/es/servicios/fotografia", label: "Fotografía" },
          { to: "/es/sobre-nosotros", label: "Sobre Nosotros" },
          { to: "/es/contratame", label: "Contrátame" },
          { to: "/es/contacto", label: "Contacto" },
        ];

  const ctaText = language === "en" ? "Send a Message" : "Enviar un mensaje";
  const ctaPath = language === "en" ? "/contact" : "/es/contacto";

  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Keyboard handler — Escape closes; Tab is trapped inside the open menu so
  // keyboard users can't accidentally tab into the (visually hidden) page
  // behind the modal.
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setMobileMenuOpen(false);
      return;
    }
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      // Move focus into the panel after the open animation kicks off so screen
      // readers announce the dialog rather than read the still-focused trigger.
      const t = setTimeout(() => closeButtonRef.current?.focus(), 50);
      return () => {
        clearTimeout(t);
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
    // On close, return focus to the trigger so keyboard users land where they were.
    toggleButtonRef.current?.focus();
    return undefined;
  }, [mobileMenuOpen, handleKeyDown]);

  // Check if a link is active
  const isActiveLink = (path: string): boolean => {
    if (path === "/" || path === "/es") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-dark-950/95 backdrop-blur-md shadow-lg shadow-black/20"
            : "bg-dark-950"
        }`}
      >
        {/* Metro stripe accent at top */}
        <div className="h-1 w-full bg-gradient-to-r from-metro-red-600 via-metro-orange-500 to-metro-yellow-400" />

        <Container size="xl">
          <nav className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link
              to={language === "en" ? "/" : "/es"}
              className="relative z-10 flex items-center gap-2 group"
            >
              <div className="flex flex-col">
                <span className="text-xl lg:text-2xl font-bold tracking-tight text-dark-50 group-hover:text-metro-orange-500 transition-colors duration-300">
                  Market
                  <span className="text-metro-orange-500 group-hover:text-dark-50 transition-colors duration-300">
                    Brewer
                  </span>
                </span>
                <span className="text-[10px] lg:text-xs text-dark-400 tracking-widest uppercase -mt-1">
                  Marketing · Web · SEO · Social
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-8">
              <ul className="flex items-center gap-1">
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                        isActiveLink(link.to)
                          ? "text-metro-orange-500"
                          : "text-dark-300 hover:text-dark-50"
                      }`}
                    >
                      {link.label}
                      {isActiveLink(link.to) && (
                        <m.div
                          layoutId="activeNavIndicator"
                          className="absolute bottom-0 left-4 right-4 h-0.5 bg-metro-orange-500 rounded-full"
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 30,
                          }}
                        />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Language Switcher */}
              <div className="border-l border-dark-700 pl-6">
                <LanguageSwitcher />
              </div>

              {/* CTA Button — drives to the contact form (the funnel entry point) */}
              <Link
                to={ctaPath}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-dark-950 bg-metro-orange-500 rounded-lg hover:bg-metro-orange-400 active:bg-metro-orange-600 transition-colors duration-200 shadow-lg shadow-metro-orange-500/20"
              >
                {ctaText}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </svg>
              </Link>
            </div>

            {/* Mobile Controls */}
            <div className="xl:hidden relative z-10 flex items-center gap-3">
              <div className="transition-transform duration-200 ease-out hover:scale-[1.02]">
                <LanguageSwitcher />
              </div>
              <button
                ref={toggleButtonRef}
                type="button"
                className="p-2 -mr-2 text-dark-300 hover:text-dark-50 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                <div className="w-6 h-6 relative">
                  <span
                    className={`absolute left-0 block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                      mobileMenuOpen ? "top-[11px] rotate-45" : "top-1"
                    }`}
                  />
                  <span
                    className={`absolute left-0 top-[11px] block w-6 h-0.5 bg-current transition-all duration-300 ${
                      mobileMenuOpen ? "opacity-0 translate-x-2" : "opacity-100"
                    }`}
                  />
                  <span
                    className={`absolute left-0 block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                      mobileMenuOpen ? "top-[11px] -rotate-45" : "top-[19px]"
                    }`}
                  />
                </div>
              </button>
            </div>
          </nav>
        </Container>

        {/* Bottom-of-nav metro stripe — mirrors the top stripe in a cooler palette */}
        <div className="h-1 w-full bg-gradient-to-r from-metro-blue-500 via-metro-green-500 to-metro-orange-500" />
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop — slightly heavier blur for separation from the page */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-0 z-40 bg-dark-950/85 backdrop-blur-md xl:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Mobile Menu Panel — slide-in from right with snappy spring */}
            <m.div
              ref={panelRef}
              id="mobile-menu"
              initial={reduceMotion ? { opacity: 0 } : { x: "100%" }}
              animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { x: "100%" }}
              transition={
                reduceMotion
                  ? { duration: 0.12 }
                  : { type: "spring", damping: 22, stiffness: 280, mass: 0.9 }
              }
              className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-dark-900 shadow-2xl shadow-black/40 xl:hidden flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label={language === "en" ? "Mobile menu" : "Menú móvil"}
            >
              {/* Metro stripe at top */}
              <div className="h-1 w-full bg-gradient-to-r from-metro-red-600 via-metro-orange-500 to-metro-yellow-400 flex-shrink-0" />

              {/* Scrollable body — keeps long menus usable on short screens */}
              <div className="flex flex-col flex-1 pt-5 pb-6 px-6 overflow-y-auto">
                {/* Top bar with eyebrow + close button */}
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-metro-orange-500">
                    {language === "en" ? "Menu" : "Menú"}
                  </span>
                  <button
                    ref={closeButtonRef}
                    type="button"
                    className="inline-flex items-center justify-center w-10 h-10 -mr-2 rounded-lg text-dark-300 hover:text-dark-50 hover:bg-dark-800/60 active:bg-dark-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label={
                      language === "en" ? "Close menu" : "Cerrar menú"
                    }
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 6l12 12M18 6l-12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Top CTA — drive to the form, matching the rest of the site */}
                <div className="mt-5">
                  <Link
                    to={ctaPath}
                    className="flex items-center justify-center gap-2 w-full px-5 py-3 text-base font-semibold text-dark-950 bg-metro-orange-500 rounded-lg hover:bg-metro-orange-400 active:bg-metro-orange-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-300 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {ctaText}
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                      />
                    </svg>
                  </Link>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 mt-8" aria-label={language === "en" ? "Primary" : "Principal"}>
                  <ul className="space-y-1">
                    {navLinks.map((link, index) => {
                      const active = isActiveLink(link.to);
                      return (
                        <m.li
                          key={link.to}
                          initial={reduceMotion ? false : { opacity: 0, x: 16 }}
                          animate={reduceMotion ? undefined : { opacity: 1, x: 0 }}
                          transition={{
                            delay: reduceMotion ? 0 : 0.08 + index * 0.05,
                            ease: [0.16, 1, 0.3, 1],
                            duration: 0.4,
                          }}
                        >
                          <Link
                            to={link.to}
                            className={`relative block pl-5 pr-4 py-3 text-lg font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500 ${
                              active
                                ? "text-metro-orange-500 bg-dark-800"
                                : "text-dark-200 hover:text-dark-50 hover:bg-dark-800/50"
                            }`}
                            onClick={() => setMobileMenuOpen(false)}
                            aria-current={active ? "page" : undefined}
                          >
                            {/* Accent bar — additional visual cue beyond color (a11y) */}
                            <span
                              className={`absolute left-0 top-2 bottom-2 w-1 rounded-r-full transition-all ${
                                active ? "bg-metro-orange-500" : "bg-transparent"
                              }`}
                              aria-hidden="true"
                            />
                            {link.label}
                          </Link>
                        </m.li>
                      );
                    })}
                  </ul>
                </nav>

                {/* Direct contact — tap-to-call + tap-to-email */}
                <div className="pt-6 mt-6 border-t border-dark-800">
                  <p className="mb-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-dark-500">
                    {language === "en" ? "Direct contact" : "Contacto directo"}
                  </p>
                  <div className="space-y-1">
                    <a
                      href={`mailto:${CONTACT.EMAIL}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-dark-200 hover:text-dark-50 hover:bg-dark-800/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500"
                    >
                      <svg
                        className="w-4 h-4 flex-shrink-0 text-metro-orange-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                        />
                      </svg>
                      <span>{CONTACT.EMAIL}</span>
                    </a>
                  </div>
                </div>
              </div>
            </m.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header (nav row + top stripe + bottom stripe) */}
      <div className="h-[72px] lg:h-[88px]" />
    </>
  );
};

export default Header;
